import React, { useEffect, useMemo, useState } from 'react';
import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import Chart from 'react-apexcharts';
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Stack,
  LinearProgress,
  Chip,
  Divider,
  Tooltip,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material';

import GroupsIcon from '@mui/icons-material/Groups';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FlagIcon from '@mui/icons-material/Flag';

import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { useDispatch, useSelector } from 'react-redux';
// 👉 Adjust import path to match your slice location
import { getHeadAnalytics } from '../../../../redux/features/taskManagementSlice';
import { getUserUnder } from '../../../../redux/features/generalSlice';
import { AccessTime, ErrorOutline, HourglassBottom, Add, Close } from '@mui/icons-material';

// 🌈 Glass style
const glass = {
  borderRadius: '22px',
  background: 'rgba(255,255,255,0.7)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.6)',
  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
  p: 2,
};

const PRIORITY_STYLES = {
  Critical: { color: '#ef4444', glow: '0 0 8px #ef4444' },
  High: { color: '#f97316', glow: '0 0 8px #f97316' },
  Medium: { color: '#eab308', glow: '0 0 8px #eab308' },
  Low: { color: '#22c55e', glow: '0 0 8px #22c55e' },
};

const userColor = (name) => {
  const palette = ['#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#8b5cf6', '#ef4444', '#22c55e', '#0ea5e9'];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
};

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const HeadAnalytics = () => {
  const dispatch = useDispatch();
  // Date range - opt-in, hidden and unset until the user turns it on, same
  // as My Tasks. Nothing is hidden by default the way the old mandatory
  // month filter did.
  const [dateRangeEnabled, setDateRangeEnabled] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedUser, setSelectedUser] = useState({
    userID: 'all',
    name: 'All Users',
  });

  // 🔥 Read from Redux
  const { headAnalytics, analyticsLoading } = useSelector((state) => state.taskManagement);
  const { dataUserUnder } = useSelector((state) => state.general);

  // 🔥 Fetch whenever the user or date range changes
  useEffect(() => {
    dispatch(
      getHeadAnalytics({
        user_id: selectedUser?.userID === 'all' ? '' : selectedUser?.userID,
        from_date: dateRangeEnabled && fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : undefined,
        to_date: dateRangeEnabled && toDate ? dayjs(toDate).format('YYYY-MM-DD') : undefined,
      }),
    );
  }, [dispatch, selectedUser, dateRangeEnabled, fromDate, toDate]);

  useEffect(() => {
    dispatch(getUserUnder());
  }, [dispatch]);

  // ✅ Safely read array from redux (supports both shapes: array or { data: [] })
  const apiData = useMemo(() => {
    if (Array.isArray(headAnalytics)) return headAnalytics;
    if (Array.isArray(headAnalytics?.data)) return headAnalytics.data;
    return [];
  }, [headAnalytics]);

  // 📊 Aggregated metrics
  const metrics = useMemo(() => {
    const total = apiData.reduce((s, u) => s + (u.total_tasks || 0), 0);
    const pending = apiData.reduce((s, u) => s + (u.pending_tasks || 0), 0);
    const completed = apiData.reduce((s, u) => s + (u.completed_tasks || 0), 0);
    const inprogress = apiData.reduce((s, u) => s + (u.inprogress_tasks || 0), 0);
    const overdue = apiData.reduce((s, u) => s + (u.overdue_tasks || 0), 0);
    const totalUsers = apiData.length;
    const activeUsers = apiData.filter((u) => (u.total_tasks || 0) > 0).length;
    const idleUsers = totalUsers - activeUsers;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    const priorityMap = {};
    apiData.forEach((u) =>
      (u.priorityData || []).forEach((p) => {
        const k = p.approval_priority_name;
        priorityMap[k] = (priorityMap[k] || 0) + (p.count || 0);
      }),
    );

    return { total, pending, completed, inprogress, totalUsers, activeUsers, idleUsers, completionRate, priorityMap };
  }, [apiData]);

  const leaderboard = useMemo(
    () => [...apiData].sort((a, b) => (b.total_tasks || 0) - (a.total_tasks || 0)).slice(0, 6),
    [apiData],
  );

  const cards = [
    {
      label: 'Team Members',
      value: metrics.totalUsers,
      sub: `${metrics.activeUsers} active • ${metrics.idleUsers} idle`,
      icon: <GroupsIcon />,
      gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
      glow: 'radial-gradient(circle,#6366f133,transparent)',
    },
    {
      label: 'Total Tasks',
      value: metrics.total,
      sub: 'Across entire team',
      icon: <AutoGraphIcon />,
      gradient: 'linear-gradient(135deg, #0ea5e9, #22d3ee)',
      glow: 'radial-gradient(circle,#0ea5e933,transparent)',
    },
    {
      label: 'Completed',
      value: metrics.completed,
      sub: `${metrics.completionRate}% completion`,
      icon: <TaskAltIcon />,
      gradient: 'linear-gradient(135deg, #22c55e, #4ade80)',
      glow: 'radial-gradient(circle,#22c55e33,transparent)',
    },
    {
      label: 'In Progress',
      value: metrics.inprogress,
      sub: metrics.inprogress > 0 ? 'Currently working on' : 'All tasks completed',
      icon: <AccessTime />,
      gradient: 'linear-gradient(135deg, #3989c2, #60a5fa)',
      glow: 'radial-gradient(circle,#3989c233,transparent)',
    },
    {
      label: 'Pending',
      value: metrics.pending,
      sub: 'Awaiting action',
      icon: <HourglassBottom />,
      gradient: 'linear-gradient(135deg, #f59e0b, #f97316)',
      glow: 'radial-gradient(circle,#f59e0b33,transparent)',
    },
    {
      label: 'Overdue',
      value: metrics.overdue || 0,
      sub: 'Tasks past due date',
      icon: <ErrorOutline />,
      gradient: 'linear-gradient(135deg, #ef4444, #f87171)',
      glow: 'radial-gradient(circle,#ef444433,transparent)',
    }
  ];

  const radialOptions = {
    chart: { type: 'radialBar' },
    plotOptions: {
      radialBar: {
        hollow: { size: '65%' },
        track: { background: '#eef2ff' },
        dataLabels: {
          name: { fontSize: '13px', color: '#64748b' },
          value: { fontSize: '26px', fontWeight: 700 },
        },
      },
    },
    colors: ['#6366f1'],
    labels: ['Team Completion'],
  };

  const donutOptions = {
    chart: { type: 'donut' },
    labels: ['Pending', 'Completed', 'In Progress', 'Overdue'],
    colors: ['#f59e0b', '#22c55e', '#3989c2', '#ef4444'],
    legend: { position: 'bottom' },
    stroke: { width: 0 },
  };
  const donutSeries = [metrics.pending, metrics.completed, metrics.inprogress, metrics.overdue || 0];

  const usersWithTasks = apiData.filter((u) => (u.total_tasks || 0) > 0);

  const userBarOptions = {
    chart: { toolbar: { show: false }, stacked: true },
    plotOptions: { bar: { borderRadius: 6, columnWidth: '55%' } },
    xaxis: { categories: usersWithTasks.map((u) => u.user_name) },
    colors: ['#f59e0b', '#22c55e', '#3989c2' , '#ef4444'],
    legend: { position: 'top' },
    grid: { borderColor: '#e5e7eb' },
    dataLabels: { enabled: false },
  };

  const userBarSeries = [
    { name: 'Pending', data: usersWithTasks.map((u) => u.pending_tasks || 0) },
    { name: 'Completed', data: usersWithTasks.map((u) => u.completed_tasks || 0) },
    { name: 'In Progress', data: usersWithTasks.map((u) => u.inprogress_tasks || 0) },
    { name: 'Overdue', data: usersWithTasks.map((u) => u.overdue_tasks || 0) },
  ];

  const priorityKeys = Object.keys(metrics.priorityMap);
  const priorityBarOptions = {
    chart: { toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 8, columnWidth: '45%', distributed: true } },
    xaxis: { categories: priorityKeys },
    colors: priorityKeys.map((k) => PRIORITY_STYLES[k]?.color || '#6366f1'),
    grid: { borderColor: '#e5e7eb' },
    dataLabels: { enabled: true },
    legend: { show: false },
  };
  const priorityBarSeries = [{ name: 'Tasks', data: priorityKeys.map((k) => metrics.priorityMap[k]) }];

  return (
    <GlobalWrapper
      title="Head Analytics"
      actions={
        <Box mb={2} display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
          {analyticsLoading && <CircularProgress size={18} sx={{ color: '#6366f1' }} />}
          <Autocomplete
            size="small"
            sx={{ minWidth: 250 }}
            options={dataUserUnder || []}
            value={selectedUser}
            onChange={(event, newValue) => {
              setSelectedUser(
                newValue || {
                  userID: 'all',
                  name: 'All Users',
                },
              );
            }}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option.userID === value.userID}
            renderInput={(params) => <TextField {...params} label="Select User" placeholder="Search user..." />}
          />
          {!dateRangeEnabled ? (
            <Button
              size="small"
              startIcon={<Add sx={{ fontSize: 16 }} />}
              onClick={() => setDateRangeEnabled(true)}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#64748B' }}
            >
              Filter by created date
            </Button>
          ) : (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <DatePicker
                  label="From"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  format="DD MMM YYYY"
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="To"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  format="DD MMM YYYY"
                  minDate={fromDate || undefined}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <Tooltip title="Clear date filter">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDateRangeEnabled(false);
                      setFromDate(null);
                      setToDate(null);
                    }}
                  >
                    <Close sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </LocalizationProvider>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          minHeight: '100vh',
          p: 3,
          borderRadius: 2,
          background: 'radial-gradient(circle at top left,#eef2ff,#f8fafc)',
          position: 'relative',
        }}
      >
        {/* Loading overlay */}
        {analyticsLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.4)',
              backdropFilter: 'blur(2px)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
            }}
          >
            <Stack alignItems="center" spacing={1}>
              <CircularProgress sx={{ color: '#6366f1' }} />
              <Typography fontSize={13} color="text.secondary" fontWeight={600}>
                Loading analytics…
              </Typography>
            </Stack>
          </Box>
        )}

        {/* 🚨 Team-wide Priority Pills */}
        {priorityKeys.length > 0 && (
          <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1.5} mb={2}>
            {priorityKeys.map((name, i) => {
              const style = PRIORITY_STYLES[name] || PRIORITY_STYLES.Low;
              return (
                <Box
                  key={i}
                  sx={{
                    px: 2,
                    py: 0.7,
                    borderRadius: '999px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: `1.5px solid ${style.color}`,
                    color: style.color,
                    background: `linear-gradient(135deg, ${style.color}12, transparent)`,
                    backdropFilter: 'blur(6px)',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    boxShadow: `0 4px 12px ${style.color}22`,
                    transition: '0.25s',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.05)',
                      background: `${style.color}20`,
                      boxShadow: `0 6px 18px ${style.color}40`,
                    },
                  }}
                >
                  <Box
                    sx={{ width: 8, height: 8, borderRadius: '50%', background: style.color, boxShadow: style.glow }}
                  />
                  {name}
                  <Box
                    sx={{
                      px: 1,
                      py: '2px',
                      borderRadius: '999px',
                      fontSize: 11,
                      fontWeight: 700,
                      background: `${style.color}20`,
                      color: style.color,
                    }}
                  >
                    {metrics.priorityMap[name]}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* 🔥 Gradient KPI Cards */}
        <Grid container spacing={3}>
          {cards.map((c, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Paper sx={{ ...glass, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', width: 140, height: 140, background: c.glow, top: -50, right: -50 }} />
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box
                    sx={{
                      background: c.gradient,
                      p: 1.25,
                      borderRadius: '14px',
                      color: '#fff',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                      display: 'flex',
                    }}
                  >
                    {c.icon}
                  </Box>
                  <IconButton size="small">
                    <ArrowOutwardIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography mt={2} variant="body2" color="text.secondary">
                  {c.label}
                </Typography>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{
                    background: c.gradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {c.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.sub}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* 📊 Charts Row 1 */}
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} md={4}>
            <Paper sx={glass}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <TrendingUpIcon sx={{ color: '#6366f1' }} />
                <Typography fontWeight={600}>Team Completion</Typography>
              </Stack>
              <Chart options={radialOptions} series={[metrics.completionRate]} type="radialBar" height={250} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={glass}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <PendingActionsIcon sx={{ color: '#f59e0b' }} />
                <Typography fontWeight={600}>Task Distribution</Typography>
              </Stack>
              <Chart options={donutOptions} series={donutSeries} type="donut" height={250} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={glass}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <FlagIcon sx={{ color: '#ef4444' }} />
                <Typography fontWeight={600}>Priority Breakdown</Typography>
              </Stack>
              {priorityKeys.length > 0 ? (
                <Chart options={priorityBarOptions} series={priorityBarSeries} type="bar" height={250} />
              ) : (
                <Box height={250} display="flex" alignItems="center" justifyContent="center">
                  <Typography color="text.secondary" fontSize={13}>
                    No priority data
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* 📊 Charts Row 2 — User workload + Leaderboard */}
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} md={7}>
            <Paper sx={glass}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <GroupsIcon sx={{ color: '#0ea5e9' }} />
                <Typography fontWeight={600}>User Workload</Typography>
                <Chip
                  size="small"
                  label={`${usersWithTasks.length} active`}
                  sx={{ ml: 1, fontWeight: 600, bgcolor: '#e0f2fe', color: '#0369a1' }}
                />
              </Stack>
              {usersWithTasks.length > 0 ? (
                <Chart options={userBarOptions} series={userBarSeries} type="bar" height={300} />
              ) : (
                <Box height={300} display="flex" alignItems="center" justifyContent="center">
                  <Stack alignItems="center" spacing={1}>
                    <PersonOffIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
                    <Typography color="text.secondary" fontSize={13}>
                      No active users
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper sx={glass}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <EmojiEventsIcon sx={{ color: '#f59e0b' }} />
                <Typography fontWeight={600}>Workload Leaderboard</Typography>
              </Stack>

              <Stack spacing={1.25}>
                {leaderboard.map((u, i) => {
                  const color = userColor(u.user_name);
                  const pct = metrics.total > 0 ? Math.round(((u.total_tasks || 0) / metrics.total) * 100) : 0;
                  const completionPct =
                    (u.total_tasks || 0) > 0 ? Math.round(((u.completed_tasks || 0) / u.total_tasks) * 100) : 0;

                  return (
                    <Box
                      key={u.user_id}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #ffffff, #f8fafc)',
                        border: '1px solid rgba(0,0,0,0.05)',
                        transition: '0.25s',
                        '&:hover': { transform: 'translateX(4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box sx={{ position: 'relative' }}>
                          <Avatar sx={{ bgcolor: color, width: 36, height: 36, fontSize: 13, fontWeight: 700 }}>
                            {getInitials(u.user_name)}
                          </Avatar>
                          {i < 3 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -4,
                                right: -4,
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                background: ['#fbbf24', '#94a3b8', '#d97706'][i],
                                color: '#fff',
                                fontSize: 10,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid #fff',
                              }}
                            >
                              {i + 1}
                            </Box>
                          )}
                        </Box>

                        <Box flex={1} minWidth={0}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography fontSize={13} fontWeight={600} noWrap>
                              {u.user_name}
                            </Typography>
                            <Typography fontSize={12} fontWeight={700} color={color}>
                              {u.total_tasks}
                            </Typography>
                          </Stack>
                          <Tooltip title={`${completionPct}% completed`}>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              sx={{
                                mt: 0.5,
                                height: 6,
                                borderRadius: 3,
                                bgcolor: `${color}22`,
                                '& .MuiLinearProgress-bar': {
                                  background: `linear-gradient(90deg, ${color}, ${color}aa)`,
                                },
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}

                {leaderboard.length === 0 && (
                  <Typography color="text.secondary" fontSize={13} textAlign="center" py={2}>
                    No users to rank
                  </Typography>
                )}
              </Stack>

              {metrics.idleUsers > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonOffIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                    <Typography fontSize={12} color="text.secondary">
                      <strong>{metrics.idleUsers}</strong> user{metrics.idleUsers > 1 ? 's' : ''} with no tasks
                    </Typography>
                  </Stack>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </GlobalWrapper>
  );
};

export default HeadAnalytics;
